import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useChannel } from '@/context/ChannelContext'
import { fireCelebration } from '@/lib/confetti'
import { getDashboardSummary, type DashboardSummary } from '@/api/dashboard'
import type { TimeseriesPeriod } from '@/api/timeseries'
import { MetricCard } from '@/components/MetricCard'
import { SubscribersChart } from '@/components/SubscribersChart'
import { BalanceCard } from '@/components/BalanceCard'
import { DashboardHero } from '@/components/DashboardHero'
import { TopSourcesTable } from '@/components/dashboard/TopSourcesTable'

export function DashboardPage() {
  const { selectedChannel, channels, isLoading: channelsLoading } = useChannel()
  const channelUUID = selectedChannel?.uuid || ''
  const [period, setPeriod] = useState<TimeseriesPeriod>('month')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (!channelsLoading && channels.length === 0) {
      navigate('/onboarding', { replace: true })
    }
  }, [channelsLoading, channels.length, navigate])

  useEffect(() => {
    if (searchParams.get('welcome') === '1' && !channelsLoading && channels.length > 0) {
      fireCelebration()
      setTimeout(() => fireCelebration(), 800)
      const next = new URLSearchParams(searchParams)
      next.delete('welcome')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, channelsLoading, channels.length])

  const { data, isLoading: summaryLoading, error } = useQuery({
    queryKey: ['dashboard', channelUUID],
    queryFn: () => getDashboardSummary(channelUUID),
    enabled: !!channelUUID,
    staleTime: 60_000,
  })

  const isLoading = channelsLoading || summaryLoading

  return (
    <div className="dashboard">
      <DashboardHero
        channelTitle={selectedChannel?.title}
        subscribers={data?.metrics.subscribers.value}
        er={data?.metrics.er.value}
        period={period}
        onPeriodChange={setPeriod}
        updatedAgo="Данные обновлены сейчас"
      />

      {!channelsLoading && !selectedChannel && (
        <div className="dashboard-state">У вас нет подключённых каналов</div>
      )}

      {isLoading && selectedChannel && <div className="dashboard-state">Загрузка метрик…</div>}
      {error && <div className="dashboard-state error">Не удалось загрузить дашборд</div>}

      {data && <MetricsGrid summary={data} />}

      {channelUUID && data && (
        <>
          <div className="dashboard-grid">
            <SubscribersChart channelUUID={channelUUID} period={period} />
            <div className="side-panel">
              <BalanceCard channelUUID={channelUUID} />
            </div>
          </div>

          <TopSourcesTable channelUUID={channelUUID} period={period} />
        </>
      )}

      <style>{`
        .dashboard {
          width: 100%;
        }
        .dashboard-state {
          padding: 32px;
          text-align: center;
          color: var(--tg-mute, #8f9eb0);
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          margin-bottom: 24px;
        }
        .dashboard-state.error { color: #d33; }
        .dashboard-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 16px;
          margin-top: 16px;
        }
        @media (max-width: 1100px) {
          .dashboard-grid { grid-template-columns: 1fr; }
        }
        .side-panel {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* KPI strip — фикс 4 колонки как в эталоне */
        .kpi-strip {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 24px;
        }
        @media (max-width: 1024px) {
          .kpi-strip { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 600px) {
          .kpi-strip { grid-template-columns: 1fr; }
        }
        .kpi {
          padding: 22px 24px;
          border-right: 1px solid var(--tg-line-2, #f0f0f4);
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
          position: relative;
          transition: background 200ms;
        }
        .kpi:nth-child(4n) { border-right: 0; }
        .kpi:nth-last-child(-n+4) { border-bottom: 0; }
        .kpi:hover { background: var(--tg-surface-2, #f5f5f9); }
        .kpi.featured {
          background: linear-gradient(180deg, var(--tg-blue-bg, #ebf2ff) 0%, transparent 100%);
        }
        .kpi.featured::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 3px;
          background: var(--tg-blue, #0080ff);
          box-shadow: 0 0 12px rgba(0, 128, 255, 0.45);
        }
        .kpi-label {
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--tg-mute, #8f9eb0);
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .kpi-label-help {
          width: 12px; height: 12px;
          opacity: 0.5;
          cursor: help;
        }
        .kpi-value {
          font-weight: 800;
          font-size: 32px;
          line-height: 1;
          letter-spacing: -0.025em;
          color: var(--tg-ink, #111);
          font-variant-numeric: tabular-nums;
          margin-bottom: 8px;
          display: flex;
          align-items: baseline;
          gap: 4px;
        }
        .kpi-value-unit {
          font-size: 16px;
          font-weight: 600;
          color: var(--tg-mute, #8f9eb0);
        }
        .kpi-delta {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-weight: 600;
          font-size: 12px;
          font-variant-numeric: tabular-nums;
        }
        .kpi-delta.up { color: #00b86b; }
        .kpi-delta.dn { color: #d33; }
        .kpi-delta.zero { color: var(--tg-mute, #8f9eb0); }
        .kpi-delta-arrow { width: 10px; height: 10px; }
        .kpi-delta-period {
          color: var(--tg-mute, #8f9eb0);
          font-weight: 500;
          margin-left: 2px;
        }
        .kpi-mini-deltas {
          margin-top: 8px;
          display: flex;
          gap: 6px;
          font-size: 11px;
          color: var(--tg-mute, #8f9eb0);
        }
        .kpi.empty .kpi-value { color: var(--tg-mute, #c4c4c8); }
        .kpi-empty-hint {
          font-size: 11px;
          color: var(--tg-mute, #8f9eb0);
          font-style: italic;
        }
      `}</style>
    </div>
  )
}

function MetricsGrid({ summary }: { summary: DashboardSummary }) {
  const m = summary.metrics
  const order: Array<{ key: keyof typeof m; featured?: boolean }> = [
    { key: 'subscribers', featured: true },
    { key: 'totalSpend' },
    { key: 'avgPdpCost' },
    { key: 'botStarts' },
    { key: 'publications' },
    { key: 'viewsPerPost' },
    { key: 'reactionsPerPost' },
    { key: 'er' },
    { key: 'err' },
  ]

  const visible = order.filter(({ key }) => m[key].value !== null)

  return (
    <div className="kpi-strip">
      {visible.map(({ key, featured }) => (
        <MetricCard key={key} metric={m[key]} featured={featured} />
      ))}
    </div>
  )
}
